import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { posthog } from '../lib/posthog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

const ROLE_ROUTES = {
  consumer: '/dashboard',
  executor: '/dashboard/executor',
  b2b_funeral: '/partners/dashboard',
  b2b_solicitor: '/partners/dashboard',
  b2b_accountant: '/partners/dashboard',
  b2b_bank_partner: '/partners/dashboard',
  b2b_owner: '/partners/dashboard',
  b2b_member: '/partners/dashboard',
  super_admin: '/dashboard/admin',
  admin: '/dashboard/admin',
  estate_reviewer: '/dashboard/admin'
};

// Global 402 handler - will be set by PlanLimitProvider
let global402Handler = null;
export const setGlobal402Handler = (handler) => { global402Handler = handler; };

// B2B roles that need organisation data
const B2B_ROLES = ['b2b_funeral', 'b2b_solicitor', 'b2b_accountant', 'b2b_bank_partner', 'b2b_owner', 'b2b_member'];

// Check if user should have B2B access (either by role OR by org membership)
const hasB2BAccess = (userData) => {
  if (!userData) return false;
  if (B2B_ROLES.includes(userData.role)) return true;
  if (userData.has_b2b_membership) return true;
  return false;
};

// Helper to create a fetch with timeout
const fetchWithTimeout = async (url, options = {}, timeoutMs = 5000) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, { ...options, signal: controller.signal });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
};

export function AuthProvider({ children }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const loadingRef = useRef(true);
  const [activeRole, setActiveRole] = useState(() => localStorage.getItem('safekept_active_role'));
  const [accessToken, setAccessToken] = useState(() => localStorage.getItem('safekept_token'));
  const [refreshToken, setRefreshToken] = useState(() => localStorage.getItem('safekept_refresh'));
  const [organisation, setOrganisation] = useState(null);
  const [orgLoading, setOrgLoading] = useState(false);
  const [featureFlags, setFeatureFlags] = useState({});
  // MFA / session-timeout state (security compliance)
  const [tempToken, setTempToken] = useState(null);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);

  // Keep loadingRef in sync with loading state to avoid stale closure in timeout
  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  const saveTokens = useCallback((access, refresh) => {
    setAccessToken(access);
    setRefreshToken(refresh);
    if (access) localStorage.setItem('safekept_token', access);
    else localStorage.removeItem('safekept_token');
    if (refresh) localStorage.setItem('safekept_refresh', refresh);
    else localStorage.removeItem('safekept_refresh');
  }, []);

  const fetchUser = useCallback(async (token) => {
    try {
      // Fetch user with 15 second timeout (increased from 5s to prevent premature timeout)
      const res = await fetchWithTimeout(`${API}/users/me`, {
        headers: { Authorization: `Bearer ${token}` }
      }, 15000);
      
      if (res.ok) {
        const data = await res.json();
        setUser(data);
        
        // Fetch organisation for B2B users - with timeout and error handling
        // This is non-blocking - user can proceed even if this fails
        if (hasB2BAccess(data)) {
          setOrgLoading(true);
          try {
            const orgRes = await fetchWithTimeout(`${API}/b2b/organisation`, {
              headers: { Authorization: `Bearer ${token}` }
            }, 10000); // 10 second timeout for org fetch (increased from 3s)
            
            if (orgRes.ok) {
              const orgData = await orgRes.json();
              setOrganisation(orgData);
            }
          } catch (e) {
            // Log but don't block - user can still access the app
            console.error('Failed to fetch organisation (timeout or error):', e);
          } finally {
            setOrgLoading(false);
          }
        }
        
        return data;
      }
      return null;
    } catch (e) {
      console.error('Failed to fetch user:', e);
      return null;
    }
  }, []);

  const refresh = useCallback(async () => {
    if (!refreshToken) return null;
    try {
      const res = await fetchWithTimeout(`${API}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken })
      }, 5000);
      
      if (res.ok) {
        const data = await res.json();
        saveTokens(data.access_token, data.refresh_token);
        return data.access_token;
      }
    } catch (e) {
      console.error('Token refresh failed:', e);
    }
    return null;
  }, [refreshToken, saveTokens]);

  useEffect(() => {
    let mounted = true;
    
    // SSO session listener – fires when Supabase SAML redirect returns
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        const _existingToken = localStorage.getItem('safekept_token');
        const _currentUserId = _existingToken ? (() => {
          try { return JSON.parse(atob(_existingToken.split('.')[1]))?.sub; } catch { return null; }
        })() : null;
        const _newUserId = session?.user?.id;
        const _isNewOrDifferentUser = !_existingToken || (_currentUserId && _newUserId && _currentUserId !== _newUserId);

        if (event === 'SIGNED_IN' && session && _isNewOrDifferentUser) {
          // SSO user returned from IdP – copy their token into safekept_token
          // so that the existing apiCall function works unchanged
          saveTokens(session.access_token, session.refresh_token);

          // Call JIT provision endpoint to ensure profile + org membership exist
          try {
            const API_URL = process.env.REACT_APP_BACKEND_URL;
            const provRes = await fetch(`${API_URL}/api/auth/sso/provision`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${session.access_token}`
              }
            });
            if (provRes.ok) {
              const userData = await provRes.json();
              setUser(userData);
              if (hasB2BAccess(userData)) {
                try {
                  const orgRes = await fetch(`${API_URL}/api/b2b/organisation`, {
                    headers: { Authorization: `Bearer ${session.access_token}` }
                  });
                  if (orgRes.ok) {
                    const orgData = await orgRes.json();
                    setOrganisation(orgData);
                  }
                } catch (orgErr) {
                  console.error('[SSO] Org fetch failed:', orgErr);
                }
              }
            }
          } catch (provErr) {
            console.error('[SSO] JIT provision failed:', provErr);
          }
        }

        if (event === 'SIGNED_OUT') {
          // Only clear if this was an SSO session (no safekept_token = SSO user)
          if (!localStorage.getItem('safekept_token')) {
            setUser(null);
            setOrganisation(null);
          }
        }
      }
    );
    
    const init = async () => {
      try {
        if (accessToken) {
          const userData = await fetchUser(accessToken);
          if (!userData && mounted) {
            const newToken = await refresh();
            if (newToken && mounted) {
              await fetchUser(newToken);
            }
          }
        }
      } catch (e) {
        console.error('Auth init error:', e);
      } finally {
        // ALWAYS set loading to false, regardless of success or failure
        if (mounted) {
          setLoading(false);
        }
      }
    };
    
    // Set a hard timeout - if init doesn't complete in 30 seconds, force loading to false
    const hardTimeout = setTimeout(() => {
      if (mounted && loadingRef.current) {
        console.warn('Auth init timeout - forcing loading to false');
        setLoading(false);
      }
    }, 30000);
    
    init();
    
    return () => {
      mounted = false;
      clearTimeout(hardTimeout);
      subscription.unsubscribe();
    };
  }, []); // eslint-disable-line

  // Fetch feature flags after user loads
  useEffect(() => {
    if (!user || !accessToken) return;
    fetchWithTimeout(`${API}/users/feature-flags`, {
      headers: { Authorization: `Bearer ${accessToken}` }
    }, 3000)
      .then(res => res.ok ? res.json() : null)
      .then(data => { if (data?.flags) setFeatureFlags(data.flags); })
      .catch(() => {}); // Silently fail – default to empty (features still render)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, accessToken]);

  // Identify the authenticated user to PostHog so analytics can be tied to the
  // Supabase user ID. Runs only once PostHog has been loaded and consent given
  // (PostHog is opted out by default until the cookie banner accept button is
  // clicked - see App.js).
  useEffect(() => {
    if (!user?.id) return;
    if (typeof window === 'undefined') return;
    if (!window.__posthog_initialised) return;
    try {
      posthog.identify(user.id, {
        email: user.email,
        role: user.role,
      });
    } catch (_) {
      /* PostHog identify failure must never break auth flow */
    }
  }, [user?.id, user?.email, user?.role]);

  const login = async (email, password) => {
    const res = await fetch(`${API}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.detail || 'Login failed');
    // If 2FA required, return without setting tokens
    if (json.requires_2fa) {
      return json;
    }
    // If TOTP MFA required (security compliance flow), stash temp_token
    // and redirect to the MFA challenge page. No tokens are set yet.
    if (json.mfa_required) {
      setTempToken(json.temp_token);
      try { navigate('/auth/mfa-challenge'); } catch (_) { /* ignore */ }
      return json;
    }
    saveTokens(json.access_token, json.refresh_token);
    setUser(json.user);
    // Fetch organisation for B2B users immediately after login
    if (hasB2BAccess(json.user)) {
      setOrgLoading(true);
      try {
        const orgRes = await fetchWithTimeout(`${API}/b2b/organisation`, {
          headers: { Authorization: `Bearer ${json.access_token}` }
        }, 10000);
        if (orgRes.ok) {
          const orgData = await orgRes.json();
          setOrganisation(orgData);
        }
      } catch (e) {
        console.error('Post-login org fetch failed:', e);
      } finally {
        setOrgLoading(false);
      }
    }
    return json;
  };

  const loginWithSSO = async (domain) => {
    try {
      const redirectTo = `${window.location.origin}/auth/callback`;
      const { data, error } = await supabase.auth.signInWithSSO({
        domain: domain,
        options: { redirectTo }
      });
      if (error) throw new Error(error.message || 'SSO login failed');
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      throw new Error(err.message || 'SSO login failed');
    }
  };

  const complete2FALogin = useCallback(async (userId, tempNonce, code) => {
    const res = await fetch(`${API}/auth/2fa/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId, temp_nonce: tempNonce, code })
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.detail || 'Verification failed');
    saveTokens(json.access_token, json.refresh_token);
    setUser(json.user);
    // Fetch organisation for B2B users immediately after 2FA login
    if (hasB2BAccess(json.user)) {
      setOrgLoading(true);
      try {
        const orgRes = await fetchWithTimeout(`${API}/b2b/organisation`, {
          headers: { Authorization: `Bearer ${json.access_token}` }
        }, 3000);
        if (orgRes.ok) {
          const orgData = await orgRes.json();
          setOrganisation(orgData);
        }
      } catch (e) {
        console.error('Post-2FA org fetch failed:', e);
      } finally {
        setOrgLoading(false);
      }
    }
    return json;
  }, [saveTokens]);

  const register = async (email, password, full_name, role = 'consumer', organisation_name = null, refCode = null, date_of_birth = null, account_type = 'consumer') => {
    const body = { email, password, full_name, role, account_type };
    if (organisation_name) body.organisation_name = organisation_name;
    if (date_of_birth) body.date_of_birth = date_of_birth;
    // SECTION 7: Include referral code in body if present
    if (refCode) body.referred_by_code = refCode;
    
    let url = `${API}/auth/register`;
    if (refCode) url += `?ref=${encodeURIComponent(refCode)}`;
    
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Registration failed');
    // Email-confirmation flow: backend returns { status: 'pending_confirmation', email, ... }
    // and no tokens. Do NOT auto-login - the user must click the confirmation
    // link in their inbox first. The signup page is responsible for showing the
    // "check your email" message.
    if (data.status === 'pending_confirmation') {
      return data;
    }
    // Legacy / non-confirmation paths (e.g. B2B accept-invite endpoints) still
    // return tokens directly - keep the auto-login behaviour for those.
    if (data.access_token) {
      saveTokens(data.access_token, data.refresh_token);
      setUser(data.user);
    }
    return data; // Return full response including redirect_to
  };

  const logout = useCallback(() => {
    setUser(null);
    setActiveRole(null);
    setOrganisation(null);
    setShowTimeoutWarning(false);
    setTempToken(null);
    localStorage.removeItem('safekept_active_role');
    saveTokens(null, null);
    // Clear the identified user from PostHog so subsequent sessions on the
    // same device are not attributed to the previous account.
    try {
      if (typeof window !== 'undefined' && window.__posthog_initialised) {
        posthog.reset();
      }
    } catch (_) {
      /* ignore */
    }
  }, [saveTokens]);

  // ── Security-compliance: complete MFA login by exchanging session token ──
  // Called by MFAChallengePage after a successful TOTP / recovery verification.
  const completeMFALogin = useCallback(async (sessionToken) => {
    if (!sessionToken) return null;
    saveTokens(sessionToken, null);
    try {
      const res = await fetch(`${API}/users/me`, {
        headers: { Authorization: `Bearer ${sessionToken}` },
      });
      if (res.ok) {
        const userData = await res.json();
        setUser(userData);
        return userData;
      }
    } catch (e) {
      console.error('MFA post-login user fetch failed:', e);
    }
    return null;
  }, [saveTokens]);

  // ── Security-compliance: session refresh + idle-warning ──────────────────
  const refreshSession = useCallback(async () => {
    if (!refreshToken) {
      setShowTimeoutWarning(false);
      return false;
    }
    try {
      const res = await fetch(`${API}/auth/sessions/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        if (data?.access_token) {
          saveTokens(data.access_token, refreshToken);
        }
        setShowTimeoutWarning(false);
        return true;
      }
    } catch (e) {
      console.error('Session refresh failed:', e);
    }
    return false;
  }, [refreshToken, saveTokens]);

  const SESSION_TIMEOUT_MINUTES = 60;
  const WARNING_MINUTES = 2;

  // Show a warning a couple of minutes before the backend-side idle timeout.
  useEffect(() => {
    if (!user) {
      setShowTimeoutWarning(false);
      return undefined;
    }
    const warningTime = (SESSION_TIMEOUT_MINUTES - WARNING_MINUTES) * 60 * 1000;
    const timer = setTimeout(() => setShowTimeoutWarning(true), warningTime);
    return () => clearTimeout(timer);
  }, [user]);

  const switchRole = (role) => {
    if (user?.roles?.includes(role)) {
      setActiveRole(role);
      localStorage.setItem('safekept_active_role', role);
      return ROLE_ROUTES[role] || '/dashboard';
    }
    return null;
  };

  const getActiveRole = () => {
    if (activeRole && user?.roles?.includes(activeRole)) return activeRole;
    return user?.role || 'consumer';
  };

  // Get the appropriate route for a user - considers B2B membership
  const getRoleRoute = (role) => {
    // If user has B2B membership, always route to partners dashboard
    if (user?.has_b2b_membership) {
      return '/partners/dashboard';
    }
    return ROLE_ROUTES[role] || '/dashboard';
  };

  // Update user with partial data (for local state updates)
  const updateUser = (updates) => {
    setUser(prev => prev ? { ...prev, ...updates } : null);
  };

  // Refresh user data from API (for getting fresh data after webhook updates)
  const refreshUser = useCallback(async () => {
    if (accessToken) {
      const userData = await fetchUser(accessToken);
      return userData;
    }
    return null;
  }, [accessToken, fetchUser]);

  // Refresh organisation data from API - with timeout
  const refreshOrganisation = useCallback(async () => {
    if (accessToken && user && hasB2BAccess(user)) {
      setOrgLoading(true);
      try {
        const res = await fetchWithTimeout(`${API}/b2b/organisation`, {
          headers: { Authorization: `Bearer ${accessToken}` }
        }, 3000);
        
        if (res.ok) {
          const data = await res.json();
          setOrganisation(data);
          return data;
        }
      } catch (e) {
        console.error('Failed to refresh organisation:', e);
      } finally {
        setOrgLoading(false);
      }
    }
    return null;
  }, [accessToken, user]);

  const apiCall = useCallback(async (url, options = {}) => {
    let token = accessToken;
    const makeReq = async (t) => {
      const fetchOptions = {
        method: options.method || 'GET',
        headers: {
          ...(!options.isFormData && { 'Content-Type': 'application/json' }),
          ...(t && { Authorization: `Bearer ${t}` }),
          ...(options.headers || {})
        }
      };

      if (options.body !== undefined) {
        fetchOptions.body = options.isFormData 
          ? options.body 
          : JSON.stringify(options.body);
      }

      const res = await fetch(`${API}${url}`, fetchOptions);
      
      // Auto-retry once on 502/503 (server temporarily unavailable)
      if ((res.status === 502 || res.status === 503) && !options._isRetry) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        return fetch(`${API}${url}`, { ...fetchOptions, _isRetry: true });
      }
      
      return res;
    };

    let res = await makeReq(token);
    
    // Handle 401 - attempt silent token refresh
    if (res.status === 401 && refreshToken) {
      try {
        const newToken = await refresh();
        if (newToken) {
          token = newToken;
          // CRITICAL FIX: Also refresh user data after token refresh
          // This ensures user.b2b_member_role is available for permission checks
          try {
            const userRes = await fetch(`${API}/users/me`, {
              headers: { Authorization: `Bearer ${newToken}` }
            });
            if (userRes.ok) {
              const userData = await userRes.json();
              setUser(userData);
              // Also refresh org if B2B user
              if (hasB2BAccess(userData)) {
                try {
                  const orgRes = await fetch(`${API}/b2b/organisation`, {
                    headers: { Authorization: `Bearer ${newToken}` }
                  });
                  if (orgRes.ok) {
                    const orgData = await orgRes.json();
                    setOrganisation(orgData);
                  }
                } catch (orgErr) {
                  console.error('Failed to refresh org after token refresh:', orgErr);
                }
              }
            }
          } catch (userErr) {
            console.error('Failed to refresh user after token refresh:', userErr);
          }
          res = await makeReq(newToken);
        } else {
          // Refresh failed - redirect to login
          logout();
          const currentPath = window.location.pathname + window.location.search;
          window.location.href = `/auth/login?expired=1&redirect=${encodeURIComponent(currentPath)}`;
          return res;
        }
      } catch (e) {
        // Refresh threw error - redirect to login
        logout();
        const currentPath = window.location.pathname + window.location.search;
        window.location.href = `/auth/login?expired=1&redirect=${encodeURIComponent(currentPath)}`;
        return res;
      }
    } else if (res.status === 401 && !refreshToken) {
      // No refresh token available - redirect to login
      logout();
      const currentPath = window.location.pathname + window.location.search;
      window.location.href = `/auth/login?expired=1&redirect=${encodeURIComponent(currentPath)}`;
      return res;
    }
    
    // Handle 402 Payment Required globally - but NOT for B2B users (they have their own payment handling)
    if (res.status === 402 && global402Handler) {
      const role = user?.role || activeRole || 'consumer';
      const isB2BRole = B2B_ROLES.includes(role) || user?.has_b2b_membership;
      if (!isB2BRole) {
        global402Handler(role === 'executor' ? 'executor' : 'consumer');
      }
    }
    
    return res;
  }, [accessToken, refreshToken, refresh, user, activeRole, logout]);

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithSSO, complete2FALogin, register, logout, apiCall, updateUser, refreshUser, accessToken, refreshToken, refresh, activeRole, switchRole, getActiveRole, getRoleRoute, ROLE_ROUTES, organisation, orgLoading, refreshOrganisation, featureFlags, tempToken, setTempToken, completeMFALogin, showTimeoutWarning, setShowTimeoutWarning, refreshSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
